import React, { useState, useEffect } from 'react'
import {
  ClockIcon,
  CheckCircleIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  CalendarDaysIcon,
  PlusIcon,
  TrashIcon,
  SunIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlayCircleIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline'
import api from '../utils/api'
import {
  getCurrentJalali,
  getJalaliMonthName,
  getJalaliDaysInMonth,
  jalaliToGregorianStr,
  getTodayLocalDateStr,
  formatHoursAndMinutes
} from '../utils/jalali'
import { ConfirmModal, AlertModal } from '../components/ConfirmModal'

interface TodayStatus {
  date: string
  persian_date?: string
  is_holiday: boolean
  holiday_name?: string
  attendances: any[]
  leaves: any[]
  todos: any[]
  total_worked_hours: number
  remaining_hours: number
  absent_hours?: number
  month_absent_hours?: number
  month_expected_hours?: number
  month_worked_hours?: number
  month_leave_hours?: number
  month_overtime_hours?: number
  is_late: boolean
  latest_check_in?: string
  latest_check_out?: string
}

export const Dashboard: React.FC = () => {
  const [status, setStatus] = useState<TodayStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  
  // Modals
  const [showManualModal, setShowManualModal] = useState(false)
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  
  // Real In-App Popups State
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmVariant?: 'danger' | 'primary' | 'warning'
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmVariant: 'danger',
    onConfirm: () => {}
  })

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Jalali date picker state
  const currentJalali = getCurrentJalali()
  const [selYear, setSelYear] = useState(currentJalali.jy)
  const [selMonth, setSelMonth] = useState(currentJalali.jm)
  const [selDay, setSelDay] = useState(currentJalali.jd)
  
  // Hour & Min state
  const [inHour, setInHour] = useState('09')
  const [inMin, setInMin] = useState('00')
  const [outHour, setOutHour] = useState('17')
  const [outMin, setOutMin] = useState('00')
  
  // Leave form
  const [leaveType, setLeaveType] = useState('annual')
  const [isHourlyLeave, setIsHourlyLeave] = useState(false)
  const [leaveHours, setLeaveHours] = useState('2')
  const [leaveReason, setLeaveReason] = useState('')

  // Todo input
  const [newTodoTitle, setNewTodoTitle] = useState('')

  // Ticks once a minute so the "worked so far" card stays live during an open shift
  const [nowTick, setNowTick] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 60000)
    return () => clearInterval(t)
  }, [])

  const fetchStatus = async () => {
    try {
      const res = await api.get('/attendance/today')
      setStatus(res.data)
    } catch (err) {
      console.error('Failed to fetch today status:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()
    const timer = setInterval(fetchStatus, 30000)
    return () => clearInterval(timer)
  }, [])

  const handleQuickCheckIn = async () => {
    setActionLoading(true)
    try {
      const todayStr = getTodayLocalDateStr()
      await api.post('/attendance/checkin', { date: todayStr })
      await fetchStatus()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت ورود',
        message: 'ورود شما با موفقیت ثبت گردید.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در ورود',
        message: err.response?.data?.detail || 'خطا در ثبت ورود'
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleQuickCheckOut = async () => {
    setActionLoading(true)
    try {
      const todayStr = getTodayLocalDateStr()
      await api.post('/attendance/checkout', { date: todayStr })
      await fetchStatus()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت خروج',
        message: 'خروج شما با موفقیت ثبت شد و ساعت کارکرد محاسبه گردید.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در خروج',
        message: err.response?.data?.detail || 'خطا در ثبت خروج'
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveManualShift = async () => {
    setActionLoading(true)
    try {
      const dateStr = jalaliToGregorianStr(selYear, selMonth, selDay)

      const cin = `${inHour.padStart(2, '0')}:${inMin.padStart(2, '0')}`
      const cout = `${outHour.padStart(2, '0')}:${outMin.padStart(2, '0')}`

      const inMinutes = parseInt(inHour) * 60 + parseInt(inMin)
      const outMinutes = parseInt(outHour) * 60 + parseInt(outMin)
      const diffHours = Math.max(0, (outMinutes - inMinutes) / 60)

      await api.post('/attendance/manual', {
        date: dateStr,
        check_in: cin,
        check_out: cout,
        work_hours: parseFloat(diffHours.toFixed(2))
      })

      setShowManualModal(false)
      await fetchStatus()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت تردد',
        message: 'شیفت کاری با موفقیت ثبت شد.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در ثبت شیفت',
        message: err.response?.data?.detail || 'خطا در ثبت شیفت دستی'
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveLeave = async () => {
    setActionLoading(true)
    try {
      const dateStr = jalaliToGregorianStr(selYear, selMonth, selDay)

      await api.post('/attendance/leaves', {
        date: dateStr,
        type: leaveType,
        is_hourly: isHourlyLeave,
        hours: isHourlyLeave ? parseFloat(leaveHours) : 8.0,
        reason: leaveReason || undefined
      })

      setShowLeaveModal(false)
      setLeaveReason('')
      await fetchStatus()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت مرخصی',
        message: 'درخواست مرخصی با موفقیت ثبت شد.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا در مرخصی',
        message: err.response?.data?.detail || 'خطا در ثبت مرخصی'
      })
    } finally {
      setActionLoading(false)
    }
  }

  // Delete Today Leave with In-App Modal
  const handleDeleteTodayLeave = (leaveId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف مرخصی امروز',
      message: 'آیا از حذف و لغو این مرخصی مطمئن هستید؟',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/attendance/leaves/${leaveId}`)
          await fetchStatus()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: 'مرخصی با موفقیت حذف گردید.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف',
            message: err.response?.data?.detail || 'خطا در حذف مرخصی'
          })
        }
      }
    })
  }

  // Delete Today Attendance Shift with In-App Modal
  const handleDeleteTodayAttendance = (attId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف تردد',
      message: 'آیا از حذف این تردد اطمینان دارید؟ ساعت کارکرد آن از محاسبات کسر خواهد شد.',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/attendance/${attId}`)
          await fetchStatus()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: 'تردد با موفقیت حذف گردید.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف',
            message: err.response?.data?.detail || 'خطا در حذف تردد'
          })
        }
      }
    })
  }

  const handleAddTodo = async () => {
    if (!newTodoTitle.trim()) return
    try {
      const todayStr = getTodayLocalDateStr()
      await api.post('/todos', {
        title: newTodoTitle.trim(),
        date: todayStr
      })
      setNewTodoTitle('')
      await fetchStatus()
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ثبت تسک'
      })
    }
  }

  const handleToggleTodo = async (id: number) => {
    try {
      await api.patch(`/todos/${id}/toggle`)
      await fetchStatus()
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در بروزرسانی وضعیت تسک'
      })
    }
  }

  const handleDeleteTodo = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف تسک',
      message: 'آیا از حذف این تسک اطمینان دارید؟',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/todos/${id}`)
          await fetchStatus()
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا',
            message: err.response?.data?.detail || 'خطا در حذف تسک'
          })
        }
      }
    })
  }

  const maxDays = getJalaliDaysInMonth(selYear, selMonth)
  const hoursOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const minsOptions = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '41', '45', '50', '55']

  const hasOpenCheckin = status?.attendances?.some(a => a.check_in && !a.check_out)
  const todayLeaves = status?.leaves || []

  const openShift = status?.attendances?.find(a => a.check_in && !a.check_out)

  // Minutes since midnight in Tehran, regardless of the browser timezone
  const tehranMinutesNow = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tehran',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date(nowTick))
    const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10) % 24
    const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10)
    return h * 60 + m
  }

  // Elapsed time of the shift that is still open (backend only counts closed shifts)
  const openShiftHours = (() => {
    if (!openShift?.check_in) return 0
    const [h, m] = String(openShift.check_in).substring(11, 16).split(':').map(Number)
    if (isNaN(h) || isNaN(m)) return 0
    return Math.max(0, (tehranMinutesNow() - (h * 60 + m)) / 60)
  })()

  const workedSoFar = (status?.total_worked_hours || 0) + openShiftHours
  const absentHours = status?.absent_hours ?? 0
  const monthAbsentHours = status?.month_absent_hours ?? 0
  const monthExpectedHours = status?.month_expected_hours ?? 0
  const monthWorkedHours = status?.month_worked_hours ?? 0
  const monthLeaveHours = status?.month_leave_hours ?? 0
  const monthOvertimeHours = status?.month_overtime_hours ?? 0

  // Everything that counts towards the monthly quota, and whatever is left over it
  const monthCreditedHours = monthWorkedHours + monthLeaveHours + monthOvertimeHours
  const monthSurplusHours = Math.max(0, monthCreditedHours - monthExpectedHours)

  return (
    <div className="space-y-6 pb-20">
      {/* Top Banner / Today Info */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 dark:from-emerald-800 dark:to-slate-900 rounded-3xl p-6 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center md:text-right">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-md rounded-full text-xs font-semibold">
            <CalendarDaysIcon className="w-4 h-4" />
            <span>{status?.persian_date || 'امروز'}</span>
            {status?.is_holiday && (
              <span className="bg-rose-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold mr-1">
                {status?.holiday_name ? `تعطیل - ${status.holiday_name}` : 'تعطیل'}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            داشبورد مدیریت تردد
          </h1>
          <p className="text-emerald-100 text-xs sm:text-sm max-w-md">
            ثبت سریع ورود و خروج، مرخصی ساعتی/روزانه و پیگیری فرم‌های تاخیر
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto">
          {!hasOpenCheckin ? (
            <button
              onClick={handleQuickCheckIn}
              disabled={actionLoading}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-2xl font-bold text-sm shadow-lg transition active:scale-95 disabled:opacity-50"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span>ثبت ورود سریع</span>
            </button>
          ) : (
            <button
              onClick={handleQuickCheckOut}
              disabled={actionLoading}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-2xl font-bold text-sm shadow-lg transition active:scale-95 disabled:opacity-50"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              <span>ثبت خروج سریع</span>
            </button>
          )}

          <button
            onClick={() => setShowManualModal(true)}
            className="px-4 py-3.5 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-2xl font-semibold text-xs transition"
          >
            شیفت دستی
          </button>

          <button
            onClick={() => setShowLeaveModal(true)}
            className="px-4 py-3.5 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-2xl font-semibold text-xs transition"
          >
            ثبت مرخصی
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">کارکرد امروز</span>
            <ClockIcon className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-emerald-600 dark:text-emerald-400">
            {formatHoursAndMinutes(status?.total_worked_hours)}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">کارکرد تا این لحظه</span>
            <PlayCircleIcon className={`w-5 h-5 ${openShift ? 'text-emerald-500' : 'text-slate-400'}`} />
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-slate-800 dark:text-white">
            {formatHoursAndMinutes(workedSoFar)}
          </div>
          <div className="text-[10px] text-slate-400">
            {openShift ? 'شیفت باز - در حال شمارش' : 'شیفت بازی ثبت نشده'}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">زمان غیبت امروز</span>
            <ArrowTrendingDownIcon className={`w-5 h-5 ${absentHours > 0 ? 'text-rose-500' : 'text-emerald-500'}`} />
          </div>
          <div className={`text-lg sm:text-xl font-extrabold ${absentHours > 0 ? 'text-rose-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {formatHoursAndMinutes(absentHours)}
          </div>
          <div className="text-[10px] text-slate-400">
            {status?.is_holiday ? 'روز تعطیل' : 'کسری از سقف روزانه'}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">مانده تا سقف کارکرد</span>
            <SparklesIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-lg sm:text-xl font-extrabold text-slate-800 dark:text-white">
            {formatHoursAndMinutes(status?.remaining_hours)}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">وضعیت تاخیر ورود</span>
            {status?.is_late ? (
              <ExclamationTriangleIcon className="w-5 h-5 text-rose-500" />
            ) : (
              <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
            )}
          </div>
          <div className="text-base sm:text-lg font-bold">
            {status?.is_late ? (
              <span className="text-rose-500">تاخیر ثبت شد</span>
            ) : (
              <span className="text-emerald-600 dark:text-emerald-400">به‌موقع / عادی</span>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">مرخصی امروز</span>
            <SunIcon className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm sm:text-base font-bold text-slate-800 dark:text-white truncate">
              {todayLeaves.length > 0 ? (
                <span className="text-amber-600 dark:text-amber-400">
                  {todayLeaves[0].is_hourly ? `${todayLeaves[0].hours}h ساعتی` : 'روزانه'}
                </span>
              ) : (
                <span className="text-slate-400 text-sm">ندارد</span>
              )}
            </div>
            {todayLeaves.length > 0 && (
              <button
                onClick={() => handleDeleteTodayLeave(todayLeaves[0].id)}
                className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                title="لغو مرخصی امروز"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="col-span-2 bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-xs font-semibold">زمان غیبت این ماه</span>
            <CalendarDaysIcon className={`w-5 h-5 ${monthAbsentHours > 0 ? 'text-rose-500' : 'text-emerald-500'}`} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-lg sm:text-xl font-extrabold ${monthAbsentHours > 0 ? 'text-rose-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {formatHoursAndMinutes(monthAbsentHours)}
            </span>
            {monthSurplusHours > 0 && (
              <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                {formatHoursAndMinutes(monthSurplusHours)} مازاد
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-400">
            از ابتدای ماه تا دیروز (جمعه‌ها و تعطیلات کسر شده)
          </div>

          {/* How that number is reached */}
          <div className="pt-3 mt-2 border-t border-slate-100 dark:border-slate-700/60 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
            <div className="flex flex-col">
              <span className="text-slate-400">موظفی ماه</span>
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {formatHoursAndMinutes(monthExpectedHours)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-slate-400">کارکرد ثبت‌شده</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                − {formatHoursAndMinutes(monthWorkedHours)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-slate-400">مرخصی</span>
              <span className="font-bold text-amber-600 dark:text-amber-400">
                − {formatHoursAndMinutes(monthLeaveHours)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-slate-400">اضافه‌کاری</span>
              <span className="font-bold text-blue-600 dark:text-blue-400">
                − {formatHoursAndMinutes(monthOvertimeHours)}
              </span>
            </div>
            <div className="flex flex-col border-r border-slate-100 dark:border-slate-700/60 pr-2">
              <span className="text-slate-400">جمع کل</span>
              <span className={`font-bold ${monthCreditedHours >= monthExpectedHours ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
                {formatHoursAndMinutes(monthCreditedHours)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Today Leaves Banner (if exists) */}
      {todayLeaves.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-3xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2">
              <SunIcon className="w-5 h-5 text-amber-500" />
              مرخصی‌های ثبت‌شده برای امروز
            </h3>
          </div>

          <div className="space-y-2">
            {todayLeaves.map((l: any) => (
              <div
                key={l.id}
                className="flex items-center justify-between bg-white dark:bg-slate-800 p-3.5 rounded-2xl border border-amber-100 dark:border-amber-900/30"
              >
                <div>
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    {l.is_hourly ? `مرخصی ساعتی (${l.hours} ساعت)` : 'مرخصی تمام روز (روزانه)'}
                  </span>
                  {l.reason && (
                    <p className="text-[11px] text-slate-400 mt-0.5">{l.reason}</p>
                  )}
                </div>

                <button
                  onClick={() => handleDeleteTodayLeave(l.id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300 rounded-xl font-medium transition border border-rose-200 dark:border-rose-800"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                  <span>لغو مرخصی</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid: Today Details & Todos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today Shifts List */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
            <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-emerald-600" />
              ترددهای ثبت‌شده امروز
            </h2>
            <span className="text-xs text-slate-400">
              {status?.attendances?.length || 0} تردد
            </span>
          </div>

          {!status?.attendances || status.attendances.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              هنوز ورودی برای امروز ثبت نشده است.
            </div>
          ) : (
            <div className="space-y-3">
              {status.attendances.map((att, idx) => (
                <div
                  key={att.id || idx}
                  className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60 transition-all hover:border-slate-200 dark:hover:border-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold text-xs">
                      {idx + 1}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        ورود:{' '}
                        {att.check_in ? att.check_in.substring(11, 16) || att.check_in : '-'}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        خروج:{' '}
                        {att.check_out ? att.check_out.substring(11, 16) || att.check_out : 'در حال کار...'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-xl">
                      {formatHoursAndMinutes(att.work_hours)}
                    </div>
                    {att.id && (
                      <button
                        onClick={() => handleDeleteTodayAttendance(att.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-xl transition-colors"
                        title="حذف تردد"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Todos & Late Checklist */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-100 dark:border-slate-700/60 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
              <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                تسک‌ها و یادداشت امروز
              </h2>
            </div>

            {/* Todo Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTodoTitle}
                onChange={e => setNewTodoTitle(e.target.value)}
                placeholder="تسک جدید (مثلاً پر کردن فرم تاخیر)..."
                onKeyDown={e => e.key === 'Enter' && handleAddTodo()}
                className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl px-3 py-2 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-emerald-500"
              />
              <button
                onClick={handleAddTodo}
                className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl transition shadow-sm"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Todo List */}
            {!status?.todos || status.todos.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">
                تسک یا فرمی برای امروز ثبت نشده است.
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {status.todos.map(todo => (
                  <div
                    key={todo.id}
                    className={`flex items-center justify-between p-3 rounded-2xl border transition ${
                      todo.is_done
                        ? 'bg-slate-50/50 dark:bg-slate-900/20 border-slate-100 dark:border-slate-800 opacity-60'
                        : 'bg-white dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-700'
                    }`}
                  >
                    <div
                      onClick={() => handleToggleTodo(todo.id)}
                      className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0"
                    >
                      <input
                        type="checkbox"
                        checked={todo.is_done}
                        readOnly
                        className="rounded text-emerald-600 focus:ring-0 cursor-pointer"
                      />
                      <span
                        className={`text-xs font-semibold break-words min-w-0 ${
                          todo.is_done
                            ? 'line-through text-slate-400'
                            : 'text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {todo.title}
                      </span>
                    </div>

                    <button
                      onClick={() => handleDeleteTodo(todo.id)}
                      className="p-1 text-slate-300 hover:text-rose-500 transition"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual Shift Modal */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-emerald-600" />
              ثبت شیفت / تردد دستی
            </h3>

            {/* Jalali Date Selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                تاریخ شمسی:
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={selDay}
                  onChange={e => setSelDay(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {Array.from({ length: maxDays }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{d}</option>
                  ))}
                </select>

                <select
                  value={selMonth}
                  onChange={e => setSelMonth(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{getJalaliMonthName(m)}</option>
                  ))}
                </select>

                <select
                  value={selYear}
                  onChange={e => setSelYear(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {[currentJalali.jy - 1, currentJalali.jy, currentJalali.jy + 1].map(y => (
                    <option key={y} value={y} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* In & Out Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                  ساعت ورود
                </label>
                <div className="flex items-center gap-1">
                  <select
                    value={inHour}
                    onChange={e => setInHour(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-800 dark:text-slate-100"
                  >
                    {hoursOptions.map(h => <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h}</option>)}
                  </select>
                  <span>:</span>
                  <select
                    value={inMin}
                    onChange={e => setInMin(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-800 dark:text-slate-100"
                  >
                    {minsOptions.map(m => <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{m}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                  ساعت خروج
                </label>
                <div className="flex items-center gap-1">
                  <select
                    value={outHour}
                    onChange={e => setOutHour(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-800 dark:text-slate-100"
                  >
                    {hoursOptions.map(h => <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h}</option>)}
                  </select>
                  <span>:</span>
                  <select
                    value={outMin}
                    onChange={e => setOutMin(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-800 dark:text-slate-100"
                  >
                    {minsOptions.map(m => <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{m}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
              <button
                onClick={() => setShowManualModal(false)}
                className="px-4 py-2 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl font-semibold"
              >
                انصراف
              </button>
              <button
                onClick={handleSaveManualShift}
                disabled={actionLoading}
                className="px-5 py-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition shadow-sm"
              >
                {actionLoading ? 'در حال ثبت...' : 'ذخیره تردد'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <SunIcon className="w-5 h-5 text-amber-500" />
              ثبت مرخصی روزانه / ساعتی
            </h3>

            {/* Jalali Date Selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                تاریخ شمسی:
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={selDay}
                  onChange={e => setSelDay(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {Array.from({ length: maxDays }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{d}</option>
                  ))}
                </select>

                <select
                  value={selMonth}
                  onChange={e => setSelMonth(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{getJalaliMonthName(m)}</option>
                  ))}
                </select>

                <select
                  value={selYear}
                  onChange={e => setSelYear(parseInt(e.target.value))}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  {[currentJalali.jy - 1, currentJalali.jy, currentJalali.jy + 1].map(y => (
                    <option key={y} value={y} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Leave Type */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                نوع مرخصی:
              </label>
              <select
                value={leaveType}
                onChange={e => setLeaveType(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
              >
                <option value="annual" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">استحقاقی</option>
                <option value="sick" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">استعلاجی</option>
                <option value="unpaid" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">بدون حقوق</option>
              </select>
            </div>

            {/* Hourly Switch */}
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-700">
              <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                مرخصی ساعتی است؟
              </span>
              <input
                type="checkbox"
                checked={isHourlyLeave}
                onChange={e => setIsHourlyLeave(e.target.checked)}
                className="rounded text-amber-500 w-4 h-4 cursor-pointer"
              />
            </div>

            {isHourlyLeave && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  مدت زمان (ساعت):
                </label>
                <select
                  value={leaveHours}
                  onChange={e => setLeaveHours(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100"
                >
                  <option value="1" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۱ ساعت</option>
                  <option value="2" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۲ ساعت</option>
                  <option value="3" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۳ ساعت</option>
                  <option value="4" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۴ ساعت (نیم‌روز)</option>
                  <option value="5" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۵ ساعت</option>
                  <option value="6" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">۶ ساعت</option>
                </select>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                دلیل (اختیاری):
              </label>
              <input
                type="text"
                value={leaveReason}
                onChange={e => setLeaveReason(e.target.value)}
                placeholder="علت درخواست مرخصی..."
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-800 dark:text-slate-200"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
              <button
                onClick={() => setShowLeaveModal(false)}
                className="px-4 py-2 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl font-semibold"
              >
                انصراف
              </button>
              <button
                onClick={handleSaveLeave}
                disabled={actionLoading}
                className="px-5 py-2 text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-xl font-bold transition shadow-sm"
              >
                {actionLoading ? 'در حال ثبت...' : 'ثبت مرخصی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Dashboard
